import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, Ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { AxiosResponse } from 'axios';

import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { required, maxLength } from '@/utils/validators';

import { useUiStore } from '@/stores';
import { goBack } from '@/composables/useNavigation';
import { IntegrationService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';

import {
    FormAction,
    DefaultServiceResult,
    IntegrationServiceCreateResult,
    IntegrationType,
    MoskitIntegrationModel,
    MoskitIntegrationFormState,
    Permission,
} from '@/types';
import UiPanel from '@/components/UiPanel.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'MoskitIntegrationFormPage',
  props: {
    action: {
        type: String,
        default: FormAction.CREATE,
    },
},
  setup(__props, { expose: __expose }) {
  __expose();

const uiStore = useUiStore();
const route = useRoute();

const props = __props;

const formState = reactive<MoskitIntegrationFormState>({
    type: IntegrationType.Moskit,
    token: null,
    activityIds: {
        BUSY: null,
        DITCHED: null,
        ANSWERED: null,
        NO_ANSWER: null,
    },
    customFields: {
        value: null,
        fieldId: null,
        fieldType: null,
    },
    isActive: true,
});

const rules = computed<ValidationArgs<MoskitIntegrationFormState>>(() => ({
    type: { required },
    token: { required, maxLength: maxLength(250) },
    activityIds: {
        BUSY: { required },
        DITCHED: { required },
        ANSWERED: { required },
        NO_ANSWER: { required },
    },
    customFields: {
        value: {},
        fieldId: {},
        fieldType: {},
    },
    isActive: { required },
}));

const v$: Ref<Validation<ValidationArgs<MoskitIntegrationFormState>>> = useVuelidate(
    rules,
    formState,
);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getIntegration = async (id: number) => {
    await setLoading(true);
    try {
        const { data } = await IntegrationService.get<MoskitIntegrationModel>(id);
        // Map the API response to the form state
        formState.token = data.token;
        formState.activityIds.BUSY = data.activityIds.BUSY;
        formState.activityIds.DITCHED = data.activityIds.DITCHED;
        formState.activityIds.ANSWERED = data.activityIds.ANSWERED;
        formState.activityIds.NO_ANSWER = data.activityIds.NO_ANSWER;
        formState.customFields.value = data.customFields.value;
        formState.customFields.fieldId = data.customFields.fieldId;
        formState.customFields.fieldType = data.customFields.fieldType;
        formState.isActive = data.isActive;
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const onSubmit = async () => {
    const isValid = await v$.value.$validate();
    if (!isValid) return;

    await setLoading(true);

    try {
        let response: AxiosResponse<DefaultServiceResult | IntegrationServiceCreateResult>;

        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            response = await IntegrationService.update<DefaultServiceResult>(id, formState);
        } else {
            response = await IntegrationService.create<IntegrationServiceCreateResult>(formState);
        }

        toast.success(response.data.message);
        v$.value.$reset();
        goBack({ name: 'ListIntegration' });
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

onMounted(async () => {
    if (props.action === FormAction.EDIT) {
        await getIntegration(Number(route.params.id));
    }
});

const __returned__ = { uiStore, route, props, formState, rules, v$, setLoading, getIntegration, onSubmit, get Permission() { return Permission }, UiPanel }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})